import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Key, ArrowRight, AlertCircle, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JoinedPortalLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", access_code: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/joined/login`, {
        email: formData.email.toLowerCase().trim(),
        access_code: formData.access_code.trim().toUpperCase()
      });

      // Store joinee token
      localStorage.setItem("joineeToken", response.data.access_token);
      localStorage.setItem("joineeInfo", JSON.stringify(response.data.joinee));
      
      toast.success(`Welcome to Training, ${response.data.joinee.name}!`);
      navigate("/joined/dashboard");
    } catch (err) {
      const message = err.response?.data?.detail || "Login failed. Please check your credentials.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col" data-testid="joined-login-page">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <nav className="relative z-10 bg-white/5 backdrop-blur-md border-b border-white/10 py-4">
        <div className="max-w-7xl mx-auto px-6">
          <div 
            className="flex items-center gap-3 cursor-pointer w-fit"
            onClick={() => navigate("/")}
          >
            <div className="w-10 h-10 rounded bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <span className="font-playfair font-bold text-white text-lg">CI</span>
            </div>
            <div className="flex flex-col">
              <span className="font-playfair font-bold text-white text-lg leading-tight">Creative</span>
              <span className="font-dm text-xs text-purple-300 tracking-wider">INFOTECH</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
            {/* Header */}
            <div className="relative p-8 text-center">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-purple-500/30"
              >
                <GraduationCap className="w-10 h-10 text-white" />
              </motion.div>
              <h1 className="font-playfair text-3xl text-white mb-2">Training Portal</h1>
              <p className="font-dm text-white/60 text-sm">
                Begin your 4-day onboarding journey with us
              </p>
            </div>

            {/* Form */}
            <div className="p-8 pt-0">
              {error && (
                <motion.div 
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="mb-6 p-4 bg-red-500/20 border border-red-400/30 rounded-xl flex items-start gap-3"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="font-dm text-sm text-red-300">{error}</p>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block font-dm text-sm text-white/80 mb-2">
                    Email Address
                  </label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 group-focus-within:text-purple-400 transition-colors" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter your email"
                      className="w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-xl font-dm text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50 focus:bg-white/10 transition-all"
                      required
                      data-testid="joined-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-dm text-sm text-white/80 mb-2">
                    Access Code
                  </label>
                  <div className="relative group">
                    <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 group-focus-within:text-purple-400 transition-colors" />
                    <input
                      type="text"
                      value={formData.access_code}
                      onChange={(e) => setFormData({ ...formData, access_code: e.target.value.toUpperCase() })}
                      placeholder="Enter your unique code"
                      className="w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-xl font-dm text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500/50 focus:bg-white/10 transition-all uppercase tracking-widest"
                      maxLength={8}
                      required
                      data-testid="joined-code-input"
                    />
                  </div>
                  <p className="font-dm text-xs text-white/40 mt-2">
                    The access code was shared by HR via email
                  </p>
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-dm font-medium py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/30"
                  data-testid="joined-login-button"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      Start Training
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </motion.button>
              </form>

              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="font-dm text-sm text-white/40 text-center">
                  Need help?{" "}
                  <a href="mailto:info@creativeinfotech.net" className="text-purple-400 hover:text-purple-300 hover:underline transition-colors">
                    Contact HR
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Footer Info */}
          <p className="text-center font-dm text-xs text-white/30 mt-6">
            This portal is exclusively for newly joined employees of Creative Infotech
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default JoinedPortalLogin;
